import { getTools } from "@/transport/tools";
import { readUserSessionToken } from "@/storage/user-session";

export type ToolMachineStatus = {
  status: string;
  error?: string;
};

export type ToolMachine = {
  name: string;
  goos?: string;
  goarch?: string;
  applied_version: number;
  tool_status: Record<string, ToolMachineStatus>;
  status_updated_at: number;
  status_stale: boolean;
};

export type ToolManifest = {
  id: string;
  name: string;
  description: string;
  how_to_use?: string;
  kind: "builtin" | "user";
  version: number;
  runtime: {
    type: "native" | "command" | "mcp-stdio" | "mcp-http";
    url?: string;
    command?: string;
    args?: string[];
  };
  functions: Array<{ name: string; description: string; parameters: Record<string, unknown> }>;
  activation: { checks?: string[]; install?: string[]; auth_checks?: string[] };
  authentication?: { strategy: string; adapter: string };
  permissions?: string[];
};

export type ToolCatalogItem = {
  id: string;
  qualified_id: string;
  manifest: ToolManifest;
  machines: Record<string, boolean>;
  source: string;
  config?: {
    provider?: string;
    provider_options?: Array<{ id: string; name: string }>;
  };
};

export type ToolsResponse = {
  version: number;
  machines: ToolMachine[];
  tools: ToolCatalogItem[];
  web_search: {
    providers: Array<"exa" | "parallel" | "google">;
  };
};

let cachedTools: ToolsResponse | undefined;
let inflightTools: Promise<ToolsResponse> | undefined;
let toolsGeneration = 0;
let toolsScope = readUserSessionToken();

function syncToolsScope() {
  const scope = readUserSessionToken();
  if (scope === toolsScope) return;
  toolsScope = scope;
  toolsGeneration++;
  cachedTools = undefined;
  inflightTools = undefined;
}

export function clearToolsCache() {
  toolsScope = readUserSessionToken();
  toolsGeneration++;
  cachedTools = undefined;
  inflightTools = undefined;
}

export function getCachedTools() {
  syncToolsScope();
  return cachedTools;
}

export function fetchTools(options?: { force?: boolean; signal?: AbortSignal }) {
  syncToolsScope();
  if (!options?.force && inflightTools) return inflightTools;
  const generation = toolsGeneration;
  const request = getTools(options?.signal)
    .then((data) => {
      if (toolsGeneration === generation) cachedTools = data;
      return data;
    })
    .finally(() => {
      if (inflightTools === request) inflightTools = undefined;
    });
  if (!options?.force) inflightTools = request;
  return request;
}

export function preloadToolsData(options?: { signal?: AbortSignal }) {
  syncToolsScope();
  if (cachedTools || inflightTools) return inflightTools ?? Promise.resolve(cachedTools!);
  return fetchTools(options);
}
